import { useEffect, useRef } from 'react'

/**
 * Dialog konfirmasi di DALAM aplikasi, pengganti `window.confirm`.
 *
 * Kenapa bukan confirm() bawaan: browser boleh menolak menampilkannya. Chrome
 * menawarkan centang "cegah halaman ini membuat dialog lagi" begitu sebuah halaman
 * memunculkan beberapa dialog berturut-turut, dan sesudah itu confirm() langsung
 * mengembalikan false tanpa bertanya. Akibatnya tombol yang dijaga confirm() terlihat
 * rusak: diklik, tidak terjadi apa-apa, tanpa error. Aksi admin di halaman user semua
 * dijaga cara itu, jadi satu centang bisa mematikan hapus, pindah departemen, ganti
 * role, dan grant sekaligus.
 *
 * Dialog ini juga yang bisa kita kendalikan tampilannya, sementara confirm() bawaan
 * tidak bisa ditata sama sekali.
 */
export function ConfirmDialog({ open, title, message, confirmLabel = 'Lanjutkan', danger, onConfirm, onCancel }: {
  open: boolean
  title: string
  message: string
  confirmLabel?: string
  danger?: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  const confirmRef = useRef<HTMLButtonElement>(null)
  const pemicuRef = useRef<Element | null>(null)
  // onCancel biasanya arrow inline di pemanggil, jadi identitasnya berubah tiap render.
  // Menaruhnya di dependency effect membuat effect ini jalan ulang setiap induknya
  // render, dan fokus direbut kembali ke tombol utama di tengah pengguna mengetik.
  const cancelRef = useRef(onCancel)
  cancelRef.current = onCancel

  // Esc membatalkan, fokus pindah ke tombol utama saat dialog muncul supaya Enter
  // bekerja seperti dialog bawaan, dan fokus dikembalikan ke kontrol pemicunya saat
  // dialog tutup supaya posisi keyboard tidak hilang ke body.
  useEffect(() => {
    if (!open) return
    pemicuRef.current = document.activeElement
    confirmRef.current?.focus()
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') cancelRef.current() }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      const pemicu = pemicuRef.current
      if (pemicu instanceof HTMLElement && document.contains(pemicu)) pemicu.focus()
    }
  }, [open])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      role="dialog" aria-modal="true" aria-labelledby="konfirmasi-judul">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px] animate-fade-in" onClick={onCancel} />
      {/* Satu blok, tanpa pemisah: isinya cuma satu kalimat, jadi header dan footer
          terpisah hanya membuat ruang kosong di tengah kartu. */}
      <div className="relative w-full max-w-[400px] bg-white border border-[#E0DFD8] rounded-2xl shadow-2xl shadow-black/15 p-5 animate-slide-up">
        <h2 id="konfirmasi-judul" className="font-display text-[16px] font-bold text-[#111110] leading-snug">{title}</h2>
        <p className="mt-1.5 text-[13px] font-body text-[#6B6860] leading-relaxed">{message}</p>
        <div className="mt-5 flex items-center justify-end gap-2">
          {/* Dibingkai, bukan teks polos: tombol batal yang tak terlihat sebagai tombol
              membuat orang ragu dan menekan tombol berbahaya di sebelahnya. */}
          <button onClick={onCancel}
            className="px-4 py-2 rounded-xl border border-[#E0DFD8] text-sm font-body text-[#6B6860] hover:text-[#111110] hover:border-[#C0BDB6] transition-colors">
            Batal
          </button>
          <button ref={confirmRef} onClick={onConfirm}
            className={`px-4 py-2 rounded-xl text-sm font-medium font-body text-white transition-colors ${
              danger ? 'bg-[#DC2626] hover:bg-[#B91C1C]' : 'bg-[#111110] hover:bg-[#2A2A28]'
            }`}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
